/**
 * API Client
 *
 * Base fetch wrapper with authentication headers and automatic
 * 401 handling with token refresh.
 */

import { useAuthStore } from '../stores/authStore';

const API_BASE_URL = import.meta.env.VITE_API_URL || '';

interface ApiError {
  code: string;
  message: string;
  details?: Record<string, string[]>;
}

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: ApiError;
  message?: string;
}

export class ApiClientError extends Error {
  code: string;
  status: number;
  details?: Record<string, string[]>;

  constructor(code: string, message: string, status: number, details?: Record<string, string[]>) {
    super(message);
    this.name = 'ApiClientError';
    this.code = code;
    this.status = status;
    this.details = details;
  }
}

/**
 * Attempt to refresh the access token using the refresh token cookie.
 * Uses request deduplication to prevent multiple simultaneous refresh requests.
 */
let refreshPromise: Promise<string | null> | null = null;

async function refreshAccessToken(): Promise<string | null> {
  // Return existing promise if a refresh is already in progress
  if (refreshPromise) {
    return refreshPromise;
  }

  // Don't attempt refresh on public routes (defense-in-depth)
  const PUBLIC_ROUTES = ['/login', '/register', '/forgot-password', '/reset-password'];
  if (PUBLIC_ROUTES.some(route => window.location.pathname.startsWith(route))) {
    return null;
  }

  refreshPromise = (async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
        method: 'POST',
        credentials: 'include', // Send refresh token cookie
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        return null;
      }

      const data = await response.json();
      if (data.success && data.accessToken) {
        return data.accessToken;
      }
      return null;
    } catch {
      return null;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

/**
 * Core fetch function with auth handling
 */
async function fetchWithAuth<T>(
  endpoint: string,
  options: RequestInit = {},
  retry = true
): Promise<T> {
  const { accessToken, setAuth, clearAuth, user } = useAuthStore.getState();

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  if (accessToken) {
    (headers as Record<string, string>)['Authorization'] = `Bearer ${accessToken}`;
  }

  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...options,
    headers,
    credentials: 'include', // Always include cookies for refresh token
  });

  // Handle 401 - try to refresh token
  if (response.status === 401 && retry) {
    const newAccessToken = await refreshAccessToken();

    if (newAccessToken && user) {
      // Update store with new token
      setAuth(user, newAccessToken);
      // Retry the request with new token
      return fetchWithAuth<T>(endpoint, options, false);
    } else {
      // Refresh failed - clear auth and redirect to login
      clearAuth();
      window.location.href = '/login';
      throw new ApiClientError('SESSION_EXPIRED', 'Your session has expired. Please log in again.', 401);
    }
  }

  const data: ApiResponse<T> = await response.json();

  if (!response.ok || !data.success) {
    throw new ApiClientError(
      data.error?.code || 'UNKNOWN_ERROR',
      data.error?.message || data.message || 'An error occurred',
      response.status,
      data.error?.details
    );
  }

  return data.data as T;
}

/**
 * API client methods
 */
export const api = {
  get: <T>(endpoint: string) => fetchWithAuth<T>(endpoint, { method: 'GET' }),

  post: <T>(endpoint: string, body?: unknown) =>
    fetchWithAuth<T>(endpoint, {
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    }),

  patch: <T>(endpoint: string, body?: unknown) =>
    fetchWithAuth<T>(endpoint, {
      method: 'PATCH',
      body: body ? JSON.stringify(body) : undefined,
    }),

  put: <T>(endpoint: string, body?: unknown) =>
    fetchWithAuth<T>(endpoint, {
      method: 'PUT',
      body: body ? JSON.stringify(body) : undefined,
    }),

  delete: <T>(endpoint: string) => fetchWithAuth<T>(endpoint, { method: 'DELETE' }),
};

/**
 * Auth-specific API calls (don't require auth middleware)
 */
export const authApi = {
  login: async (email: string, password: string, rememberMe = false) => {
    const response = await fetch(`${API_BASE_URL}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ email, password, rememberMe }),
    });

    const data = await response.json();

    if (!response.ok || !data.success) {
      throw new ApiClientError(
        data.error?.code || 'LOGIN_FAILED',
        data.error?.message || 'Login failed',
        response.status
      );
    }

    return {
      accessToken: data.accessToken as string,
      user: {
        id: data.user.id as string,
        email: data.user.email as string,
        name: data.user.name as string,
        avatarUrl: data.user.avatar_url as string | null | undefined,
      },
    };
  },

  register: async (email: string, password: string, name: string) => {
    const response = await fetch(`${API_BASE_URL}/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ email, password, name, acceptTerms: true }),
    });

    const data = await response.json();

    if (!response.ok || !data.success) {
      throw new ApiClientError(
        data.error?.code || 'REGISTRATION_FAILED',
        data.error?.message || 'Registration failed',
        response.status
      );
    }

    return {
      user: {
        id: data.user.id as string,
        email: data.user.email as string,
        name: data.user.name as string,
      },
    };
  },

  logout: async () => {
    const { accessToken } = useAuthStore.getState();

    await fetch(`${API_BASE_URL}/auth/logout`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      },
      credentials: 'include',
    });
  },

  refreshToken: refreshAccessToken,

  getCurrentUser: async () => {
    const { accessToken } = useAuthStore.getState();

    if (!accessToken) {
      return null;
    }

    const response = await fetch(`${API_BASE_URL}/auth/me`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      credentials: 'include',
    });

    if (!response.ok) {
      return null;
    }

    const data = await response.json();

    if (!data.success) {
      return null;
    }

    return {
      id: data.user.id as string,
      email: data.user.email as string,
      name: data.user.name as string,
      avatarUrl: data.user.avatar_url as string | null | undefined,
    };
  },
};

export default api;
